import { MaybeRefOrGetter } from '@vue/reactivity'
import { computed, toValue } from 'vue'

import { useSettingStore } from '@/stores/setting'

export type DateRange = (Date | null)[] | undefined
export type DateRangeISO = (string | null)[] | undefined

function toISODate(date: Date): string {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
    .getDate()
    .toString()
    .padStart(2, '0')}`
}
function fromISODate(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function toISODateRange(dateRange: DateRange): DateRangeISO {
  if (!dateRange) return
  return [
    dateRange[0] ? toISODate(dateRange[0]) : null,
    dateRange[1] ? toISODate(dateRange[1]) : null,
  ]
}
function fromISODateRange(isoDateRange: DateRangeISO): DateRange {
  if (!isoDateRange) return
  return [
    isoDateRange[0] ? fromISODate(isoDateRange[0]) : null,
    isoDateRange[1] ? fromISODate(isoDateRange[1]) : null,
  ]
}

function toTime24(timeAsDate: Date): string {
  return `${timeAsDate.getHours().toString().padStart(2, '0')}:${timeAsDate
    .getMinutes()
    .toString()
    .padStart(2, '0')}`
}
function fromTime24(time24: string): Date {
  const [hour, minute] = time24.split(':').map(Number)
  return new Date(2000, 0, 1, hour, minute)
}

/**
 * A composable function to handle date operations.
 *
 * @param {MaybeRefOrGetter<string>} datetime - The input date string. Can be either a date in ISO format or a full datetime format.
 *   Examples:
 *     - '2023-05-15'
 *     - '2023-05-15T14:30:00'
 * @param {function} setISODate - A function to set the new date value. Always receives a date in ISO format.
 *   Example: setISODate('2023-05-15')
 * @returns {Object} An object containing:
 *   - date: A computed Date object that can be get or set.
 *     Example: { date: { value: new Date('2023-05-15') } }
 *   - dateISO: A computed string representing the date in ISO format.
 *     Example: { dateISO: { value: '2023-05-15' } }
 */
export function useDate(
  datetime: MaybeRefOrGetter<string | null>,
  // eslint-disable-next-line no-unused-vars
  setISODate: (isoDate: string | null) => void,
) {
  const settingStore = useSettingStore()
  const date = computed<Date | null>({
    get: () => {
      const value = toValue(datetime)
      if (!value) return null
      return fromISODate(settingStore.dateIsoStr(value))
    },
    set: (date: Date | null) => {
      setISODate(date ? toISODate(date) : null)
    },
  })
  const dateISO = computed<string | null>(() => (date.value ? toISODate(date.value) : null))
  return { date, dateISO }
}

/**
 * A composable function to handle date range operations.
 *
 * @param {MaybeRefOrGetter<DateRangeISO>} datetimeRange - The input date range. Can be a ref, getter, or direct value of DateRangeISO type.
 *   It can contain ISO date ranges or full datetime values or null or a mix of both.
 *   Examples:
 *     - ['2023-05-15', '2023-05-20']
 *     - ['2023-05-15T14:30:00', '2023-05-20T09:00:00']
 *     - [null, null]
 *     - ['2023-05-15', null]
 *     - [null, '2023-05-20']
 *     - ['2023-05-15T14:30:00', null]
 *     - [null, '2023-05-20T09:00:00']
 * @param {function} setter - A function to set the new date range value. Always receives a range of ISO date strings or null.
 *   Examples:
 *     - setter(['2023-05-15', '2023-05-20'])
 *     - setter([null, null])
 *     - setter(['2023-05-15', null])
 *     - setter([null, '2023-05-20'])
 *     - setter(['2023-05-15T14:30:00', null])
 *     - setter([null, '2023-05-20T09:00:00'])
 * @returns {Object} An object containing:
 *   - dateRange: A computed DateRange object that can be get or set.
 *     Examples:
 *       - { value: [new Date('2023-05-15'), new Date('2023-05-20')] }
 *       - { value: [null, null] }
 *       - { value: [new Date('2023-05-15'), null] }
 *       - { value: [null, new Date('2023-05-20')] }
 *   - dateRangeISO: A computed DateRangeISO representing the date range in ISO format.
 *     Examples:
 *       - { value: ['2023-05-15', '2023-05-20'] }
 *       - { value: [null, null] }
 *       - { value: ['2023-05-15', null] }
 *       - { value: [null, '2023-05-20'] }
 */
export function useDateRange(
  datetimeRange: MaybeRefOrGetter<DateRangeISO>,
  // eslint-disable-next-line no-unused-vars
  setter: (value: DateRangeISO) => void,
) {
  const settingStore = useSettingStore()
  const dateRange = computed<DateRange>({
    get: () => {
      if (!toValue(datetimeRange)) return
      return fromISODateRange(
        toValue(datetimeRange)?.map(isoDate => (isoDate ? settingStore.dateIsoStr(isoDate) : null)),
      )
    },
    set: (dateRange: DateRange) => {
      setter(toISODateRange(dateRange))
    },
  })
  const dateRangeISO = computed<DateRangeISO>(() => toISODateRange(dateRange.value))
  return { dateRange, dateRangeISO }
}

/**
 * A composable function to handle time operations.
 *
 * @param {MaybeRefOrGetter<string>} datetime - The input datetime string. Can be a date in ISO format or the time in 24-hour format.
 *   Examples:
 *     - '2023-05-15T14:30:00'
 *     - '14:30'
 * @param {function} setter - A function to set the new time value. Always receives a time in 24-hour format.
 *   Examples:
 *     - setter('14:30')
 * @returns {Object} An object containing:
 *   - time: A computed Date object that can be get or set. It does take into account the current date, just the time.
 *     Examples:
 *       { value: new Date('2020-01-01T14:30:00') }
 *   - time24: A computed string representing the time in 24-hour format.
 *     Examples:
 *       { value: '14:30' }
 */
export function useTime(
  datetime: MaybeRefOrGetter<string | null>,
  // eslint-disable-next-line no-unused-vars
  setter: (time24: string | null) => void,
) {
  const settingStore = useSettingStore()
  const time = computed<Date | null>({
    get: () => {
      const value = toValue(datetime)
      if (!value) return null
      if (value.includes('T')) {
        return fromTime24(settingStore.time24Str(value))
      } else {
        return fromTime24(value)
      }
    },
    set: (timeInDate: Date | null) => {
      if (!timeInDate) return
      setter(toTime24(timeInDate))
    },
  })
  const time24 = computed<string | null>(() => {
    const value = toValue(datetime)
    if (!value) return null
    if (value.includes('T')) {
      return settingStore.time24Str(value)
    } else {
      return value
    }
  })
  return { time, time24 }
}
